import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./RewindPaywall.module.scss"

// ---------------------------------------------------------------------------
// "Rewind" — the pitch is NOT privacy. It's memory resurfacing: every day,
// Premium quietly brings back a moment from your past ("On this day"), the ones
// you'd never scroll far enough to find again.
//
// The screen is a vertical scrubbable TIMELINE, not a hero + card. A fixed
// "lens" sits across the middle of a paper reel of dated memories. Drag / scroll
// and whichever memory crosses the lens is *resurfaced* — it blooms from small,
// faded and grey (forgotten) into full colour with its story. Everything off the
// lens fades back into the past. Warm, light, film-diary art direction; the
// real photos are pre-fetched into public/rewind so it renders fully offline.
// ---------------------------------------------------------------------------

const PUB = process.env.PUBLIC_URL ?? ""
const src = (file: string) => `${PUB}/rewind/${file}.jpg`

type Memory = {
  img: string
  when: string
  date: string
  cap: string
  note?: string
}

// Each is a July-1st from the user's past — an "on this day" reel.
const MEMORIES: Memory[] = [
  {
    img: "morning",
    when: "Today",
    date: "Jul 1, 2026",
    cap: "This morning’s light",
    note: "the ordinary days count too",
  },
  {
    img: "birthday",
    when: "1 year ago",
    date: "Jul 1, 2025",
    cap: "The candles you almost missed",
    note: "her fourth",
  },
  {
    img: "rain",
    when: "2 years ago",
    date: "Jul 1, 2024",
    cap: "A rainy Tuesday, nowhere to be",
  },
  {
    img: "snow",
    when: "3 years ago",
    date: "Jul 1, 2023",
    cap: "His very first snow",
  },
  {
    img: "roadtrip",
    when: "4 years ago",
    date: "Jul 1, 2022",
    cap: "The road with no plan",
  },
  {
    img: "beach",
    when: "6 years ago",
    date: "Jul 1, 2020",
    cap: "The beach nobody else found",
  },
  {
    img: "kitchen",
    when: "8 years ago",
    date: "Jul 1, 2018",
    cap: "Sunday at her kitchen table",
    note: "you can almost smell it",
  },
  {
    img: "apartment",
    when: "9 years ago",
    date: "Jul 1, 2017",
    cap: "First place, empty rooms",
  },
  {
    img: "friends",
    when: "12 years ago",
    date: "Jul 1, 2014",
    cap: "Before you all scattered",
  },
]

const ROW_H = 116 // must match .row height in the stylesheet

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v))
}

function RewindPaywall() {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : ""
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : ""
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : ""
  const savings = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 0
    return Math.round(
      (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
    )
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")

  // ── the scrub reel ──────────────────────────────────────────────────────
  const railRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const rafScroll = useRef<number | null>(null)
  const [autoScrolling, setAutoScrolling] = useState(!reduce)

  const onScroll = useCallback(() => {
    if (rafScroll.current != null) return
    rafScroll.current = requestAnimationFrame(() => {
      rafScroll.current = null
      if (railRef.current) setScrollTop(railRef.current.scrollTop)
    })
  }, [])

  // Auto-demo: glide down through the first few years so a glance teaches the
  // gesture and always shows a memory resurfacing. Cancelled the moment the
  // user touches the reel.
  const cancelAuto = useCallback(() => setAutoScrolling(false), [])
  useEffect(() => {
    if (!autoScrolling || reduce) return
    const el = railRef.current
    if (!el) return
    const from = 0
    const to = ROW_H * 4
    const dur = 3200
    let start: number | null = null
    let frame = 0
    const step = (ts: number) => {
      if (start == null) start = ts
      const t = clamp((ts - start) / dur, 0, 1)
      // easeInOutSine
      const e = -(Math.cos(Math.PI * t) - 1) / 2
      el.scrollTop = from + (to - from) * e
      if (t < 1) frame = requestAnimationFrame(step)
    }
    frame = requestAnimationFrame(step)
    return () => cancelAnimationFrame(frame)
  }, [autoScrolling, reduce])

  const activeIndex = clamp(
    Math.round(scrollTop / ROW_H),
    0,
    MEMORIES.length - 1,
  )
  const active = MEMORIES[activeIndex]

  const handleClose = () => native.postNativeEvent("close")
  const buy = () => {
    const p = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (p) native.subscribe(p)
  }

  return (
    <div className={styles.page}>
      {/* header — a diary masthead, not a wordmark hero */}
      <div className={styles.top}>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="16" height="16">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <span className={styles.masthead}>CloudVault Premium</span>
        <span className={styles.todayTag}>ON&nbsp;THIS&nbsp;DAY</span>
      </div>

      <div className={styles.head}>
        <h1 className={styles.title}>
          The days you’d have
          <br />
          <span className={styles.ital}>forgotten.</span>
        </h1>
        <p className={styles.sub}>
          Every morning, Premium slips one moment from your past back into today
          — kept in full quality, waiting for you to find it.
        </p>
      </div>

      {/* ── the star: a scrubbable reel of dated memories ──────────────── */}
      <div className={styles.reelWrap}>
        {/* the spine that runs the length of the past */}
        <div className={styles.spine} aria-hidden />

        <div
          className={`${styles.reel} ${autoScrolling ? styles.auto : ""}`}
          ref={railRef}
          onScroll={onScroll}
          onPointerDown={cancelAuto}
          onWheel={cancelAuto}
          onTouchStart={cancelAuto}
        >
          <div className={styles.pad} />
          {MEMORIES.map((m, i) => {
            const dist = Math.abs(scrollTop - i * ROW_H) / ROW_H
            const focus = clamp(1 - dist, 0, 1)
            const scale = 0.8 + 0.2 * focus
            const gray = (1 - focus) * 100
            const shift = (1 - focus) * 14 // slide toward the spine when dozing
            return (
              <div className={styles.row} key={m.img}>
                <div
                  className={styles.frame}
                  style={{
                    transform: `scale(${scale}) translateX(${shift}px)`,
                    opacity: 0.34 + 0.66 * focus,
                  }}
                >
                  <div className={styles.node} style={{ opacity: focus }} />
                  <div className={styles.photoWrap}>
                    <img
                      src={src(m.img)}
                      alt=""
                      className={styles.photo}
                      style={{
                        filter: `grayscale(${gray}%) saturate(${60 + 60 * focus}%)`,
                      }}
                      draggable={false}
                    />
                    <div
                      className={styles.resurfaced}
                      style={{
                        opacity: focus > 0.72 ? (focus - 0.72) / 0.28 : 0,
                      }}
                    >
                      resurfaced
                    </div>
                  </div>
                  <div className={styles.meta}>
                    <div className={styles.when}>{m.when}</div>
                    <div className={styles.cap}>{m.cap}</div>
                    {m.note ? (
                      <div
                        className={styles.note}
                        style={{ opacity: focus > 0.6 ? focus : 0 }}
                      >
                        {m.note}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            )
          })}
          <div className={styles.pad} />
        </div>

        {/* the lens — a fixed viewfinder the memories pass through */}
        <div className={styles.lens} aria-hidden>
          <div className={styles.lensEdge} />
          <div className={styles.lensLabel}>
            <span className={styles.lensDate}>{active.date}</span>
          </div>
          <div className={styles.lensEdge} />
        </div>

        {/* soft fades top & bottom so the reel dissolves into the page */}
        <div className={`${styles.fade} ${styles.fadeTop}`} aria-hidden />
        <div className={`${styles.fade} ${styles.fadeBot}`} aria-hidden />
      </div>

      <p className={styles.rule}>
        A camera roll buries this. Premium remembers it —{" "}
        <b>a new one every single day.</b>
      </p>

      {/* ── pricing ─────────────────────────────────────────────────────── */}
      <div className={styles.sheet}>
        <div className={styles.plans}>
          <button
            className={`${styles.planYear} ${plan === "yearly" ? styles.on : ""}`}
            onClick={() => setPlan("yearly")}
          >
            <div className={styles.planTop}>
              <span className={styles.planName}>A year of rewinds</span>
              {savings > 0 ? (
                <span className={styles.save}>Save {savings}%</span>
              ) : null}
            </div>
            <div className={styles.priceRow}>
              <span className={styles.bigPrice}>{yearlyPrice}</span>
              <span className={styles.per}>/year</span>
            </div>
            {yearlyPerMonth ? (
              <span className={styles.permo}>
                just {yearlyPerMonth}/mo — 365 memories back
              </span>
            ) : null}
          </button>

          <button
            className={`${styles.planMonth} ${plan === "monthly" ? styles.on : ""}`}
            onClick={() => setPlan("monthly")}
          >
            <span className={styles.planName}>Monthly</span>
            <span className={styles.monthPrice}>{monthlyPrice}/mo</span>
          </button>
        </div>

        <motion.button
          className={styles.cta}
          onClick={buy}
          whileTap={{ scale: 0.975 }}
        >
          Start remembering
        </motion.button>
        <p className={styles.reassure}>
          Cancel anytime. Your photos are always yours, in full quality.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
        </div>
      </div>
    </div>
  )
}

export default RewindPaywall
